import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";

// --- Data for Dropdowns ---
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg', disabled: true},
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg', disabled: true }
];
// Note: 'location' is used instead of 'region' for DataSources
const gcpLocationList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
const awsLocationList = [ "us-east-1", "us-east-2", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-northeast-3", "ap-southeast-1", "ap-southeast-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-north-1", "me-south-1", "sa-east-1"];
const azureLocationList = ["eastus", "eastus2", "westus", "westus2", "westus3", "centralus", "northcentralus", "southcentralus", "canadacentral", "canadaeast", "uksouth", "ukwest", "northeurope", "westeurope", "francecentral", "germanywestcentral", "japaneast", "japanwest", "koreacentral", "koreasouth", "australiaeast", "australiasoutheast", "southafricanorth", "eastasia", "southeastasia", "brazilsouth", "uaenorth"];

const resourceSpecTypes = [
    { value: 'STORAGE_BUCKET', label: 'STORAGE_BUCKET' },
    { value: 'BIGQUERY_DATASET', label: 'BIGQUERY_DATASET' }
];

// --- Interfaces ---
interface CostLabelPair { id?: string; cost: string; label: string; }
// Updated Form values for DataSources
interface DataSourceFormValues {
    id?: string;
    name: string;
    cloudProvider: string;
    projectId: string;
    environment: string;
    location: string;
    lake: string;
    zone: string;
    description: string;
    resourceSpecType: string;
    resourceSpecName: string;
    discoverySpecEnabled: boolean;
    csvHeaderRows: string; // Use string for Formik, parse to int on submit
    csvDelimiter: string;
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
}
interface ConfigData { gcpProjectId: string; }
interface FetchedDataSourceData { id: string; name: string; }
interface AddDataSourcesPageProps { isEdit: boolean; }
interface DataSourcesFormContentProps { isEdit: boolean; }
// Simplified Dynamic Field Config for DataSources
interface DynamicFieldConfig {
    labels: { projectId: string; };
    dropdowns: { locations: string[]; };
    defaults: { location: string; }
}

// --- Component to handle dynamic field updates ---
interface DynamicFieldsUpdaterProps {
    isEdit: boolean;
    initialFormValuesForEdit: DataSourceFormValues | null;
    configData: ConfigData | null;
    setDynamicFields: React.Dispatch<React.SetStateAction<DynamicFieldConfig>>;
}

const DynamicFieldsUpdater: React.FC<DynamicFieldsUpdaterProps> = ({ isEdit, initialFormValuesForEdit, configData, setDynamicFields }) => {
    const { values, setFieldValue } = useFormikContext<DataSourceFormValues>();

    useEffect(() => {
        let config: DynamicFieldConfig;
        switch (values.cloudProvider) {
            case 'aws':
                config = { labels: { projectId: 'Account ID' }, dropdowns: { locations: awsLocationList }, defaults: { location: 'us-east-1' } };
                break;
            case 'azure':
                config = { labels: { projectId: 'Subscription ID' }, dropdowns: { locations: azureLocationList }, defaults: { location: 'eastus' }};
                break;
            case 'gcp':
            default:
                config = { labels: { projectId: 'Project ID' }, dropdowns: { locations: gcpLocationList }, defaults: { location: 'asia-south1' }};
                break;
        }
        setDynamicFields(config);

        if (!isEdit || (initialFormValuesForEdit && values.cloudProvider !== initialFormValuesForEdit.cloudProvider)) {
            setFieldValue('location', config.defaults.location);
            setFieldValue('projectId', values.cloudProvider === 'gcp' ? (configData?.gcpProjectId || '') : '');
        }
    }, [values.cloudProvider, setFieldValue, isEdit, initialFormValuesForEdit, configData, setDynamicFields]);

    return null;
};

// --- Main Form Content Component ---
const DataSourcesFormContent: React.FC<DataSourcesFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [configData, setConfigData] = useState<ConfigData | null>(null);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<DataSourceFormValues | null>(null);
    const [isLoadingDataSourceData, setIsLoadingDataSourceData] = useState(false);
    const [availableLakes, setAvailableLakes] = useState<any[]>([]);
    const [availableZones, setAvailableZones] = useState<any[]>([]);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig>({
        labels: { projectId: 'Project ID' },
        dropdowns: { locations: gcpLocationList },
        defaults: { location: 'asia-south1' }
    });

useEffect(() => {
        const fetchInitialData = async () => {
            try {
                // Fetch Config, Lakes, and Zones in parallel
                const [configRes, lakesRes, zonesRes] = await Promise.all([
                    queryGraphql(`query GetConfig { getConfig { gcpProjectId } }`),
                    queryGraphql(`query GetDataplexLakes { getDataplexLakes { id name } }`),
                    queryGraphql(`query GetDataplexZones { getDataplexZones { id name parentSolution { id name } } }`)
                ]);

                if (configRes.getConfig) setConfigData(configRes.getConfig);
                if (lakesRes.getDataplexLakes) setAvailableLakes(lakesRes.getDataplexLakes);
                if (zonesRes.getDataplexZones) setAvailableZones(zonesRes.getDataplexZones);

            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchDataSourceForEdit = async () => {
            if (isEdit && id) {
                setIsLoadingDataSourceData(true);
                try {
                    const response = await queryGraphql(`
                        query GetDataSource($getDataSourceId: String!) {
                          getDataSource(id: $getDataSourceId) {
                            id name cloudProvider costLabelPairs { cost label }
                            details { 
                              dataSources { 
                                projectId location lake zone description
                                resourceSpec { type name }
                                discoverySpec { enabled csvOptions { headerRows delimiter } }
                              } 
                            }
                          }
                        }
                    `, { getDataSourceId: id });

                    const dataSource = response.getDataSource;
                    if (dataSource) {
                        const allPairs = dataSource.costLabelPairs || [];
                        const costCenterPair = allPairs.find((p: any) => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter((p: any) => p.cost !== "cost-center").map((pair: any, index: number) => ({ ...pair, id: `clp-edit-${index}` }));
                        const dsDetails = dataSource.details.dataSources;
                        
                        setInitialFormValuesForEdit({
                            id: dataSource.id,
                            name: dataSource.name,
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            cloudProvider: dataSource.cloudProvider || 'gcp',
                            projectId: dsDetails.projectId || "",
                            environment: dataSource.environment || "dev",
                            location: dsDetails.location || "",
                            lake: dsDetails.lake || "",
                            zone: dsDetails.zone || "",
                            description: dsDetails.description || "",
                            resourceSpecType: dsDetails.resourceSpec?.type || "STORAGE_BUCKET",
                            resourceSpecName: dsDetails.resourceSpec?.name || "",
                            discoverySpecEnabled: dsDetails.discoverySpec?.enabled || false,
                            csvHeaderRows: dsDetails.discoverySpec?.csvOptions?.headerRows?.toString() || "0",
                            csvDelimiter: dsDetails.discoverySpec?.csvOptions?.delimiter || "",
                        });
                    } else {
                        navigate("/datasolutions/data-sources");
                    }
                } catch (err) {
                    console.error("Error fetching data source for editing:", err);
                    navigate("/datasolutions/data-sources");
                } finally {
                    setIsLoadingDataSourceData(false);
                }
            }
        };
        fetchDataSourceForEdit();
    }, [isEdit, id, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        cloudProvider: Yup.string().required("This field is required"),
        projectId: Yup.string().required("This field is required"),
        environment: Yup.string().required("This field is required"),
        location: Yup.string().required("This field is required"),
        lake: Yup.string().required("Lake name is required"),
        zone: Yup.string().required("Zone name is required"),
        description: Yup.string().optional(),
        resourceSpecType: Yup.string().required("Resource type is required"),
        resourceSpecName: Yup.string().required("Resource name is required (e.g., projects/.../buckets/...)"),
        discoverySpecEnabled: Yup.boolean(),
        csvHeaderRows: Yup.number().when('discoverySpecEnabled', { is: true, then: (schema) => schema.required('Header rows are required').min(0), otherwise: (schema) => schema.optional() }),
        csvDelimiter: Yup.string().when('discoverySpecEnabled', { is: true, then: (schema) => schema.required('Delimiter is required'), otherwise: (schema) => schema.optional() }),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: DataSourceFormValues = {
        name: "",
        cloudProvider: "gcp",
        projectId: configData?.gcpProjectId || "",
        environment: "dev",
        location: "asia-south1",
        lake: "",
        zone: "",
        description: "",
        resourceSpecType: "STORAGE_BUCKET",
        resourceSpecName: "",
        discoverySpecEnabled: false,
        csvHeaderRows: "0",
        csvDelimiter: ",",
        costCenterValue: "",
        costLabelPairs: [],
    };
    
    if (isEdit && isLoadingDataSourceData) {
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading data source...</Typography></Box>;
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load data source for editing.</Typography>;
    }

    return (
        <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const detailsPayload: any = {
                        projectId: values.projectId,
                        location: values.location,
                        lake: values.lake,
                        zone: values.zone,
                        description: values.description,
                        resourceSpec: {
                            type: values.resourceSpecType,
                            name: values.resourceSpecName
                        },
                        discoverySpec: {
                            enabled: values.discoverySpecEnabled,
                            csvOptions: values.discoverySpecEnabled ? {
                                headerRows: parseInt(values.csvHeaderRows) || 0,
                                delimiter: values.csvDelimiter
                            } : null
                        }
                    };

                    const commonInput = {
                        name: values.name,
                        cloudProvider: values.cloudProvider,
                        environment: values.environment,
                        costLabelPairs: finalCostLabelPairs,
                        details: { dataSources: detailsPayload },
                    };

                    const mutationString = isEdit ? `mutation UpdateSolutionDataSources($input: UpdateSolutionInput) { updateSolutionDataSources(input: $input) { id } }` : `mutation CreateSolutionDataSources($input: CreateSolutionInput) { createSolutionDataSources(input: $input) { id } }`;
                    
                    const variables = { input: isEdit ? { id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionDataSources' : 'createSolutionDataSources';

                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/datasolutions/data-sources");
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Data Source. ${JSON.stringify(data.errors)}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => (
                    <Form>
                        <DynamicFieldsUpdater isEdit={isEdit} initialFormValuesForEdit={initialFormValuesForEdit} configData={configData} setDynamicFields={setDynamicFields} />
                        
                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                            {isEdit ? "Edit the Data Source details below" : "Fill the below details to add a Data Source"}
                        </Typography>
                        
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                    <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    disabled={p.disabled}
                                                    aria-label={p.label}
                                                    sx={{ display: 'flex', gap: 1, alignItems: 'center', textTransform: 'none', fontFamily: 'Gilroy-Medium', borderRadius: '24px !important', border: '1px solid #ccc !important', px: 2, py: 1, '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)', }, '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)', } }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Data Source Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Data Source Name" /><TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div></Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Environment" />
                                    <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>
                                        <MenuItem value="dev" sx={{ fontFamily: "Gilroy-Medium" }}>dev</MenuItem>
                                        <MenuItem value="staging" sx={{ fontFamily: "Gilroy-Medium" }}>staging</MenuItem>
                                        <MenuItem value="prod" sx={{ fontFamily: "Gilroy-Medium" }}>prod</MenuItem>
                                    </TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicFields.labels.projectId} /><TextField variant="filled" fullWidth name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicFields.labels.projectId}`} size="medium" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="projectId" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Location" /><TextField select name="location" value={values.location} onChange={(e) => setFieldValue("location", e.target.value)} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.location && Boolean(errors.location)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{dynamicFields.dropdowns.locations.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="location" /></div></Grid>
                                {/* Lake Dropdown */}
<Grid item xs={12} sm={6}>
    <CompulsoryFieldsLabel fieldName="Lake Name" />
    <TextField
        select
        variant="filled"
        fullWidth
        name="lake"
        value={values.lake}
        onChange={(e) => {
            // Update Lake and reset Zone when Lake changes
            setFieldValue("lake", e.target.value);
            setFieldValue("zone", "");
        }}
        onBlur={handleBlur}
        placeholder="Select Lake"
        size="medium"
        hiddenLabel
        error={touched.lake && Boolean(errors.lake)}
        InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}
        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}
    >
        {availableLakes.map((lake) => (
            <MenuItem key={lake.id} value={lake.name} sx={{ fontFamily: "Gilroy-Medium" }}>
                {lake.name}
            </MenuItem>
        ))}
    </TextField>
    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="lake" /></div>
</Grid>

{/* Zone Dropdown */}
<Grid item xs={12} sm={6}>
    <CompulsoryFieldsLabel fieldName="Zone Name" />
    <TextField
        select
        variant="filled"
        fullWidth
        name="zone"
        value={values.zone}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Select Zone"
        size="medium"
        hiddenLabel
        // Disable zone selection if no lake is selected
        disabled={!values.lake}
        error={touched.zone && Boolean(errors.zone)}
        InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}
        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}
    >
        {availableZones
            // Filter zones based on the selected Lake Name
            .filter((zone) => zone.parentSolution?.name === values.lake)
            .map((zone) => (
                <MenuItem key={zone.id} value={zone.name} sx={{ fontFamily: "Gilroy-Medium" }}>
                    {zone.name}
                </MenuItem>
            ))}
    </TextField>
    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="zone" /></div>
</Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Resource Spec Type" />
                                    <TextField select variant="filled" fullWidth name="resourceSpecType" value={values.resourceSpecType} onChange={handleChange} onBlur={handleBlur} size="medium" hiddenLabel error={touched.resourceSpecType && Boolean(errors.resourceSpecType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                        {resourceSpecTypes.map((type) => (<MenuItem key={type.value} value={type.value} sx={{ fontFamily: "Gilroy-Medium" }}>{type.label}</MenuItem>))}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="resourceSpecType" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Resource Spec Name" /><TextField variant="filled" fullWidth name="resourceSpecName" value={values.resourceSpecName} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., projects/.../buckets/my-bucket" size="medium" hiddenLabel error={touched.resourceSpecName && Boolean(errors.resourceSpecName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="resourceSpecName" /></div></Grid>
                                <Grid item xs={12}><CompulsoryFieldsLabel fieldName="Description"/><TextField variant="filled" fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a description for the data source" size="medium" hiddenLabel error={touched.description && Boolean(errors.description)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div></Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Discovery Specifications</Typography>
                            <Divider sx={{ mb: "24px" }} />
                             <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={<Checkbox checked={values.discoverySpecEnabled} onChange={handleChange} name="discoverySpecEnabled" sx={{ color: 'black', '&.Mui-checked': { color: 'black', } }} />}
                                        label="Enable Discovery"
                                        sx={{ fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }}
                                    />
                                </Grid>
                                {values.discoverySpecEnabled && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="CSV Header Rows" />
                                            <TextField variant="filled" fullWidth type="number" name="csvHeaderRows" value={values.csvHeaderRows} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 1" size="medium" hiddenLabel error={touched.csvHeaderRows && Boolean(errors.csvHeaderRows)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" }, inputProps: { min: 0 } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="csvHeaderRows" /></div>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CompulsoryFieldsLabel fieldName="CSV Delimiter" />
                                            <TextField variant="filled" fullWidth name="csvDelimiter" value={values.csvDelimiter} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., ," size="medium" hiddenLabel error={touched.csvDelimiter && Boolean(errors.csvDelimiter)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} />
                                            <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="csvDelimiter" /></div>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3} sx={{ mb: "16px" }}>
                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                            </Grid>
                            <FieldArray
                                name="costLabelPairs"
                                render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                    </div>
                                )}
                            />
                            <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', },mt:"1" }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mb:"24px", display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
                        </Box>
                        
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-sources")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button>
                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New Data Source")}</Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

// --- Page Component (Wrapper) ---
const AddDataSourcesPage: React.FC<AddDataSourcesPageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };
    
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-sources"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:2 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
                        <Link underline="hover" color="inherit" href="/datasources" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/datasolutions/data-sources")}}>Data Sources</Link>
                        <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>{isEdit ? "Edit Data Source" : "Add New Data Source"}</Typography>
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-sources")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit Data Source" : "Add New Data Source"}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <DataSourcesFormContent isEdit={isEdit} />
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddDataSourcesPage;



// import React, { useState, useEffect } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Divider,
//     useTheme,
//     CircularProgress,
//     Checkbox,
//     FormControlLabel,
//     Breadcrumbs,
//     Link,
//     ToggleButton,
//     ToggleButtonGroup,
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
// import * as Yup from "yup";
// import { useAuth } from "../../../helpers/AuthProvider";
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
// import queryGraphql from "../../../helpers/ApiInterface";
// import MiniBot from "../../MiniBot";
// import MainDrawer from "../../MainDrawer";

// // --- Data for Dropdowns ---
// const providerDisplayData = [
//     { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
//     { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
//     { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
// ];
// // Note: 'location' is used instead of 'region' for DataSources
// const gcpLocationList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
// const awsLocationList = [ "us-east-1", "us-east-2", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-northeast-3", "ap-southeast-1", "ap-southeast-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-north-1", "me-south-1", "sa-east-1"];
// const azureLocationList = ["eastus", "eastus2", "westus", "westus2", "westus3", "centralus", "northcentralus", "southcentralus", "canadacentral", "canadaeast", "uksouth", "ukwest", "northeurope", "westeurope", "francecentral", "germanywestcentral", "japaneast", "japanwest", "koreacentral", "koreasouth", "australiaeast", "australiasoutheast", "southafricanorth", "eastasia", "southeastasia", "brazilsouth", "uaenorth"];

// // --- Interfaces ---
// interface CostLabelPair { id?: string; cost: string; label: string; }
// // Updated Form values for DataSources
// interface DataSourceFormValues {
//     id?: string;
//     name: string;
//     applicationName: string;
//     cloudProvider: string;
//     projectId: string;
//     location: string;
//     environment: string;
//     lake: string;
//     zone: string;
//     zoneType: string;
//     description: string;
//     resourceSpecType: string;
//     resourceSpecName: string;
//     discoverySpecEnabled: boolean;
//     csvHeaderRows: string; // Use string for Formik, parse to int on submit
//     csvDelimiter: string;
//     costCenterValue: string;
//     costLabelPairs: CostLabelPair[];
// }
// interface ConfigData { gcpProjectId: string; }
// interface FetchedDataSourceData { id: string; name: string; environment: string; }
// interface ApplicationData { id: string; applicationName: string; environment: string; }
// interface AddDataSourcesPageProps { isEdit: boolean; }
// interface DataSourcesFormContentProps { isEdit: boolean; }
// // Simplified Dynamic Field Config for DataSources
// interface DynamicFieldConfig {
//     labels: { projectId: string; };
//     dropdowns: { locations: string[]; };
//     defaults: { location: string; }
// }

// // --- Component to handle dynamic field updates ---
// interface DynamicFieldsUpdaterProps {
//     isEdit: boolean;
//     initialFormValuesForEdit: DataSourceFormValues | null;
//     configData: ConfigData | null;
//     setDynamicFields: React.Dispatch<React.SetStateAction<DynamicFieldConfig>>;
// }

// const DynamicFieldsUpdater: React.FC<DynamicFieldsUpdaterProps> = ({ isEdit, initialFormValuesForEdit, configData, setDynamicFields }) => {
//     const { values, setFieldValue } = useFormikContext<DataSourceFormValues>();

//     useEffect(() => {
//         let config: DynamicFieldConfig;
//         switch (values.cloudProvider) {
//             case 'aws':
//                 config = { labels: { projectId: 'Account ID' }, dropdowns: { locations: awsLocationList }, defaults: { location: 'us-east-1' } };
//                 break;
//             case 'azure':
//                 config = { labels: { projectId: 'Subscription ID' }, dropdowns: { locations: azureLocationList }, defaults: { location: 'eastus' }};
//                 break;
//             case 'gcp':
//             default:
//                 config = { labels: { projectId: 'Project ID' }, dropdowns: { locations: gcpLocationList }, defaults: { location: 'asia-south1' }};
//                 break;
//         }
//         setDynamicFields(config);

//         if (!isEdit || (initialFormValuesForEdit && values.cloudProvider !== initialFormValuesForEdit.cloudProvider)) {
//             setFieldValue('location', config.defaults.location);
//             setFieldValue('projectId', values.cloudProvider === 'gcp' ? (configData?.gcpProjectId || '') : '');
//         }
//     }, [values.cloudProvider, setFieldValue, isEdit, initialFormValuesForEdit, configData, setDynamicFields]);

//     return null;
// };

// // --- Component to handle dynamic environment field ---
// interface EnvironmentFieldUpdaterProps {
//     applications: ApplicationData[];
//     fetchedDataSources: FetchedDataSourceData[];
//     setEnvironmentOptions: React.Dispatch<React.SetStateAction<string[]>>;
// }
// const EnvironmentFieldUpdater: React.FC<EnvironmentFieldUpdaterProps> = ({ applications, fetchedDataSources, setEnvironmentOptions }) => {
//     const { values, setFieldValue } = useFormikContext<DataSourceFormValues>();

//     useEffect(() => {
//         if (!values.applicationName) {
//             setEnvironmentOptions([]);
//             return;
//         }

//         const availableEnvs = applications
//             .filter(app => app.applicationName === values.applicationName)
//             .map(app => app.environment);

//         const existingEnvsForName = fetchedDataSources
//             .filter(p => p.name.toLowerCase() === values.name.toLowerCase())
//             .map(p => p.environment);
        
//         const finalOptions = availableEnvs.filter(env => !existingEnvsForName.includes(env));
//         setEnvironmentOptions(finalOptions);

//         if (values.environment && !finalOptions.includes(values.environment)) {
//             setFieldValue('environment', '');
//         }

//     }, [values.applicationName, values.name, applications, fetchedDataSources, setEnvironmentOptions, setFieldValue, values.environment]);

//     return null;
// }

// // --- Main Form Content Component ---
// const DataSourcesFormContent: React.FC<DataSourcesFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const [isSubmittingForm, setIsSubmittingForm] = useState(false);
//     const [configData, setConfigData] = useState<ConfigData | null>(null);
//     const [fetchedDataSources, setFetchedDataSources] = useState<FetchedDataSourceData[]>([]);
//     const [applications, setApplications] = useState<ApplicationData[]>([]);
//     const [confirmationChecked, setConfirmationChecked] = useState(false);
//     const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<DataSourceFormValues | null>(null);
//     const [isLoadingDataSourceData, setIsLoadingDataSourceData] = useState(false);
//     const [environmentOptions, setEnvironmentOptions] = useState<string[]>([]);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

//     const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig>({
//         labels: { projectId: 'Project ID' },
//         dropdowns: { locations: gcpLocationList },
//         defaults: { location: 'asia-south1' }
//     });
    
//     const uniqueApplicationNames = React.useMemo(() => {
//         return Array.from(new Set(applications.map(app => app.applicationName)));
//     }, [applications]);

//     useEffect(() => {
//         const fetchInitialData = async () => {
//             try {
//                 const [configRes, dataSourcesRes, appsRes] = await Promise.all([
//                     queryGraphql(`query GetConfig { getConfig { gcpProjectId } }`),
//                     queryGraphql(`query GetDataSources { getDataSources { id name environment } }`),
//                     queryGraphql(`query GetApplications { applications { id applicationName environment } }`)
//                 ]);

//                 if (configRes.getConfig) setConfigData(configRes.getConfig);
//                 if (dataSourcesRes.getDataSources) setFetchedDataSources(dataSourcesRes.getDataSources.map((p: any) => ({ id: p.id, name: p.name.toLowerCase(), environment: p.environment })));
//                 if (appsRes.applications) setApplications(appsRes.applications);
                
//             } catch (error) {
//                 console.error("Error fetching initial data:", error);
//             }
//         };
//         fetchInitialData();
//     }, []);

//     useEffect(() => {
//         const fetchDataSourceForEdit = async () => {
//             if (isEdit && id) {
//                 setIsLoadingDataSourceData(true);
//                 try {
//                     const response = await queryGraphql(`
//                         query GetDataSource($getDataSourceId: String!) {
//                           getDataSource(id: $getDataSourceId) {
//                             id name cloudProvider environment application { applicationName } costLabelPairs { cost label }
//                             details { 
//                               dataSources { 
//                                 projectId location lake zone zoneType description
//                                 resourceSpec { type name }
//                                 discoverySpec { enabled csvOptions { headerRows delimiter } }
//                               } 
//                             }
//                           }
//                         }
//                     `, { getDataSourceId: id });

//                     const dataSource = response.getDataSource;
//                     if (dataSource) {
//                         const allPairs = dataSource.costLabelPairs || [];
//                         const costCenterPair = allPairs.find((p: any) => p.cost === "cost-center");
//                         const dynamicPairs = allPairs.filter((p: any) => p.cost !== "cost-center").map((pair: any, index: number) => ({ ...pair, id: `clp-edit-${index}` }));
//                         const dsDetails = dataSource.details.dataSources;
                        
//                         setInitialFormValuesForEdit({
//                             id: dataSource.id,
//                             name: dataSource.name,
//                             applicationName: dataSource.application?.applicationName || "",
//                             costCenterValue: costCenterPair ? costCenterPair.label : "",
//                             costLabelPairs: dynamicPairs,
//                             cloudProvider: dataSource.cloudProvider || 'gcp',
//                             environment: dataSource.environment || "",
//                             projectId: dsDetails.projectId || "",
//                             location: dsDetails.location || "",
//                             lake: dsDetails.lake || "",
//                             zone: dsDetails.zone || "",
//                             zoneType: dsDetails.zoneType || "",
//                             description: dsDetails.description || "",
//                             resourceSpecType: dsDetails.resourceSpec?.type || "",
//                             resourceSpecName: dsDetails.resourceSpec?.name || "",
//                             discoverySpecEnabled: dsDetails.discoverySpec?.enabled || false,
//                             csvHeaderRows: dsDetails.discoverySpec?.csvOptions?.headerRows?.toString() || "0",
//                             csvDelimiter: dsDetails.discoverySpec?.csvOptions?.delimiter || "",
//                         });
//                     } else {
//                         navigate("/datasolutions/data-sources");
//                     }
//                 } catch (err) {
//                     console.error("Error fetching data source for editing:", err);
//                     navigate("/datasolutions/data-sources");
//                 } finally {
//                     setIsLoadingDataSourceData(false);
//                 }
//             }
//         };
//         fetchDataSourceForEdit();
//     }, [isEdit, id, navigate]);

//     const validationSchema = Yup.object().shape({
//         name: Yup.string().required("This field is required").max(50, "Max 50 characters"),
//         applicationName: Yup.string().required("This field is required"),
//         environment: Yup.string().required("This field is required"), 
//         cloudProvider: Yup.string().required("This field is required"),
//         projectId: Yup.string().required("This field is required"),
//         location: Yup.string().required("This field is required"),
//         lake: Yup.string().required("Lake name is required"),
//         zone: Yup.string().required("Zone name is required"),
//         zoneType: Yup.string().required("Zone type is required"),
//         description: Yup.string().optional(),
//         resourceSpecType: Yup.string().required("Resource type is required (e.g., STORAGE_BUCKET)"),
//         resourceSpecName: Yup.string().required("Resource name is required (e.g., projects/.../buckets/...)"),
//         discoverySpecEnabled: Yup.boolean(),
//         csvHeaderRows: Yup.number().when('discoverySpecEnabled', { is: true, then: (schema) => schema.required('Header rows are required').min(0), otherwise: (schema) => schema.optional() }),
//         csvDelimiter: Yup.string().when('discoverySpecEnabled', { is: true, then: (schema) => schema.required('Delimiter is required'), otherwise: (schema) => schema.optional() }),
//         costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
//         costLabelPairs: Yup.array().of(Yup.object().shape({
//             cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
//             label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
//         })),
//     });

//     const defaultInitialValues: DataSourceFormValues = {
//         name: "",
//         applicationName: "",
//         cloudProvider: "gcp",
//         projectId: configData?.gcpProjectId || "",
//         location: "asia-south1",
//         environment: "", 
//         lake: "",
//         zone: "",
//         zoneType: "",
//         description: "",
//         resourceSpecType: "STORAGE_BUCKET",
//         resourceSpecName: "",
//         discoverySpecEnabled: false,
//         csvHeaderRows: "0",
//         csvDelimiter: ",",
//         costCenterValue: "",
//         costLabelPairs: [],
//     };
    
//     if (isEdit && isLoadingDataSourceData) {
//         return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading data source...</Typography></Box>;
//     }
//     if (isEdit && !initialFormValuesForEdit) {
//          return <Typography color="error" sx={{p:3}}>Could not load data source for editing.</Typography>;
//     }

//     return (
//         <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
//             <Formik
//                 initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
//                 validationSchema={validationSchema}
//                 enableReinitialize={true}
//                 onSubmit={async (values, { setSubmitting, setStatus }) => {
//                     setIsSubmittingForm(true);
//                     setSubmitting(true);

//                     const selectedApplication = applications.find(app => app.applicationName.toLowerCase() === values.applicationName.toLowerCase() && app.environment === values.environment);
//                     const applicationId = selectedApplication ? selectedApplication.id : null;
                    
//                     const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

//                     const detailsPayload: any = {
//                         projectId: values.projectId,
//                         location: values.location,
//                         lake: values.lake,
//                         zone: values.zone,
//                         zoneType: values.zoneType,
//                         description: values.description,
//                         resourceSpec: {
//                             type: values.resourceSpecType,
//                             name: values.resourceSpecName
//                         },
//                         discoverySpec: {
//                             enabled: values.discoverySpecEnabled,
//                             csvOptions: values.discoverySpecEnabled ? {
//                                 headerRows: parseInt(values.csvHeaderRows) || 0,
//                                 delimiter: values.csvDelimiter
//                             } : null
//                         }
//                     };

//                     const commonInput = {
//                         name: values.name,
//                         // solutionCategoryType: "dataplex", // Or remove if not needed
//                         cloudProvider: values.cloudProvider,
//                         environment: values.environment, 
//                         costLabelPairs: finalCostLabelPairs,
//                         details: { dataSources: detailsPayload }, // Key change: `dataSources`
//                         ...(applicationId && { application: applicationId })
//                     };

//                     const mutationString = isEdit ? `mutation UpdateSolutionDataSources($input: UpdateSolutionInput) { updateSolutionDataSources(input: $input) { id } }` : `mutation CreateSolutionDataSources($input: CreateSolutionInput) { createSolutionDataSources(input: $input) { id } }`;
                    
//                     const variables = { input: isEdit ? { id, ...commonInput } : commonInput };
//                     const resultKey = isEdit ? 'updateSolutionDataSources' : 'createSolutionDataSources';

//                     try {
//                         const data = await queryGraphql(mutationString, variables);
//                         if (data[resultKey] && data[resultKey].id) {
//                             navigate("/datasolutions/data-sources");
//                         } else {
//                             setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Data Source. ${JSON.stringify(data.errors)}` });
//                         }
//                     } catch (error: any) {
//                         setStatus({ error: error.message || "An unexpected error occurred." });
//                     } finally {
//                         setIsSubmittingForm(false);
//                         setSubmitting(false);
//                     }
//                 }}
//             >
//                 {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => (
//                     <Form>
//                         <DynamicFieldsUpdater isEdit={isEdit} initialFormValuesForEdit={initialFormValuesForEdit} configData={configData} setDynamicFields={setDynamicFields} />
//                         <EnvironmentFieldUpdater applications={applications} fetchedDataSources={fetchedDataSources} setEnvironmentOptions={setEnvironmentOptions} />

//                         <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
//                             {isEdit ? "Edit the Data Source details below" : "Fill the below details to add a Data Source"}
//                         </Typography>
                        
//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <Grid container spacing={3}>
//                                 <Grid item xs={12}>
//                                     <CompulsoryFieldsLabel fieldName="Cloud Provider" />
//                                     <ToggleButtonGroup
//                                             value={values.cloudProvider}
//                                             exclusive
//                                             onChange={(event, newProvider) => {
//                                                 if (newProvider !== null) {
//                                                     setFieldValue('cloudProvider', newProvider);
//                                                 }
//                                             }}
//                                             aria-label="cloud provider"
//                                             sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
//                                         >
//                                             {providerDisplayData.map((p) => (
//                                                 <ToggleButton
//                                                     key={p.value}
//                                                     value={p.value}
//                                                     aria-label={p.label}
//                                                     sx={{ display: 'flex', gap: 1, alignItems: 'center', textTransform: 'none', fontFamily: 'Gilroy-Medium', borderRadius: '24px !important', border: '1px solid #ccc !important', px: 2, py: 1, '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)', }, '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)', } }}
//                                                 >
//                                                     <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
//                                                     {p.label}
//                                                 </ToggleButton>
//                                             ))}
//                                         </ToggleButtonGroup>
//                                 </Grid>
//                             </Grid>
//                         </Box>

//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Data Source Details</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <Grid container spacing={3}>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Data Source Name" /><TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div></Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Application Name" />
//                                     <TextField select variant="filled" fullWidth name="applicationName" value={values.applicationName} onChange={handleChange} onBlur={handleBlur} size="medium" hiddenLabel error={touched.applicationName && Boolean(errors.applicationName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
//                                         {uniqueApplicationNames.map((appName) => (<MenuItem key={appName} value={appName} sx={{ fontFamily: "Gilroy-Medium" }}>{appName}</MenuItem>))}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="applicationName" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <CompulsoryFieldsLabel fieldName="Environment" />
//                                     <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" disabled={!values.applicationName || !values.name} error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}
//                                         SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}
//                                     >
//                                         {environmentOptions.map((env) => (<MenuItem key={env} value={env} sx={{ fontFamily: "Gilroy-Medium" }}>{env}</MenuItem>))}
//                                     </TextField>
//                                     <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div>
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicFields.labels.projectId} /><TextField variant="filled" fullWidth name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicFields.labels.projectId}`} size="medium" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="projectId" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Location" /><TextField select name="location" value={values.location} onChange={(e) => setFieldValue("location", e.target.value)} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.location && Boolean(errors.location)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{dynamicFields.dropdowns.locations.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="location" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Lake Name" /><TextField variant="filled" fullWidth name="lake" value={values.lake} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., my-lake" size="medium" hiddenLabel error={touched.lake && Boolean(errors.lake)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="lake" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Zone Name" /><TextField variant="filled" fullWidth name="zone" value={values.zone} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., my-raw-zone" size="medium" hiddenLabel error={touched.zone && Boolean(errors.zone)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="zone" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Zone Type" /><TextField variant="filled" fullWidth name="zoneType" value={values.zoneType} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., RAW or CURATED" size="medium" hiddenLabel error={touched.zoneType && Boolean(errors.zoneType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="zoneType" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Resource Spec Type" /><TextField variant="filled" fullWidth name="resourceSpecType" value={values.resourceSpecType} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., STORAGE_BUCKET" size="medium" hiddenLabel error={touched.resourceSpecType && Boolean(errors.resourceSpecType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="resourceSpecType" /></div></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Resource Spec Name" /><TextField variant="filled" fullWidth name="resourceSpecName" value={values.resourceSpecName} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., projects/.../buckets/my-bucket" size="medium" hiddenLabel error={touched.resourceSpecName && Boolean(errors.resourceSpecName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="resourceSpecName" /></div></Grid>
//                                  <Grid item xs={12}><CompulsoryFieldsLabel fieldName="Description"/><TextField variant="filled" fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a description for the data source" size="medium" hiddenLabel error={touched.description && Boolean(errors.description)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div></Grid>
//                             </Grid>
//                         </Box>

//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Discovery Specifications</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                              <Grid container spacing={3}>
//                                 <Grid item xs={12}>
//                                     <FormControlLabel
//                                         control={<Checkbox checked={values.discoverySpecEnabled} onChange={handleChange} name="discoverySpecEnabled" sx={{ color: 'black', '&.Mui-checked': { color: 'black', } }} />}
//                                         label="Enable Discovery"
//                                         sx={{ fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }}
//                                     />
//                                 </Grid>
//                                 {values.discoverySpecEnabled && (
//                                     <>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="CSV Header Rows" />
//                                             <TextField variant="filled" fullWidth type="number" name="csvHeaderRows" value={values.csvHeaderRows} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 1" size="medium" hiddenLabel error={touched.csvHeaderRows && Boolean(errors.csvHeaderRows)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" }, inputProps: { min: 0 } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="csvHeaderRows" /></div>
//                                         </Grid>
//                                         <Grid item xs={12} sm={6}>
//                                             <CompulsoryFieldsLabel fieldName="CSV Delimiter" />
//                                             <TextField variant="filled" fullWidth name="csvDelimiter" value={values.csvDelimiter} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., ," size="medium" hiddenLabel error={touched.csvDelimiter && Boolean(errors.csvDelimiter)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} />
//                                             <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="csvDelimiter" /></div>
//                                         </Grid>
//                                     </>
//                                 )}
//                             </Grid>
//                         </Box>

//                         <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
//                             <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
//                             <Divider sx={{ mb: "24px" }} />
//                             <Grid container spacing={3} sx={{ mb: "16px" }}>
//                                 <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
//                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
//                             </Grid>
//                             <FieldArray
//                                 name="costLabelPairs"
//                                 render={arrayHelpers => (
//                                     <div>
//                                         {values.costLabelPairs.map((pair, index) => (
//                                             <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
//                                                 <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
//                                                 <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
//                                                 <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
//                                             </Grid>
//                                         ))}
//                                         <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
//                                     </div>
//                                 )}
//                             />
//                             <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', },mt:"1" }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mb:"24px", display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
//                         </Box>
                        
//                         <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
//                             <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-sources")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button>
//                             <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New Data Source")}</Button>
//                         </Box>
//                     </Form>
//                 )}
//             </Formik>
//         </Container>
//     );
// };

// // --- Page Component (Wrapper) ---
// const AddDataSourcesPage: React.FC<AddDataSourcesPageProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const theme = useTheme();
//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//         if (typeof window !== 'undefined') {
//             localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
//         }
//     };
    
//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"datasolutions-data-sources"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
//                 <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:2 }}>
//                     <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
//                         <Link underline="hover" color="inherit" href="/datasources" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/datasolutions/data-sources")}}>Data Sources</Link>
//                         <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>{isEdit ? "Edit Data Source" : "Add New Data Source"}</Typography>
//                     </Breadcrumbs>
//                     <Box display="flex" alignItems="center" mt={1.5}>
//                         <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-sources")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
//                         <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit Data Source" : "Add New Data Source"}</Typography>
//                     </Box>
//                 </Box>
//                 <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
//                     <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
//                         <DataSourcesFormContent isEdit={isEdit} />
//                     </Box>
//                 </Box>
//                 <MiniBot />
//             </Box>
//         </Box>
//     );
// };

// export default AddDataSourcesPage;